import React, { useState } from 'react';
import { Question, Assessment } from '../types';
import { apiService } from '../services/api';
import { 
  UserGroupIcon, 
  CheckIcon, 
  XMarkIcon,
  ClockIcon 
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface QuestionDelegationProps {
  questions: Question[];
  assessment: Assessment;
  onQuestionsDelegated: () => void;
  onClose?: () => void;
}

const QuestionDelegation: React.FC<QuestionDelegationProps> = ({
  questions,
  assessment,
  onQuestionsDelegated,
  onClose
}) => {
  const [selectedQuestions, setSelectedQuestions] = useState<Set<string>>(new Set());
  const [isDelegating, setIsDelegating] = useState(false);
  const [showDelegationForm, setShowDelegationForm] = useState(true);
  const [delegationNotes, setDelegationNotes] = useState('');

  const handleQuestionToggle = (questionId: string) => {
    const newSelected = new Set(selectedQuestions);
    if (newSelected.has(questionId)) {
      newSelected.delete(questionId);
    } else {
      newSelected.add(questionId);
    }
    setSelectedQuestions(newSelected);
  };

  const handleDelegateQuestions = async () => {
    if (selectedQuestions.size === 0) {
      toast.error('Please select at least one question to delegate');
      return;
    }

    try {
      setIsDelegating(true);
      
      // Create delegation for each selected question
      const delegationPromises = Array.from(selectedQuestions).map(questionId => 
        apiService.post('/delegated-questions', {
          assessmentId: assessment.id,
          questionId,
          clientId: assessment.clientId,
          advisorId: assessment.advisorId,
          notes: delegationNotes || undefined
        })
      );

      await Promise.all(delegationPromises);
      
      toast.success(`${selectedQuestions.size} questions delegated successfully`);
      setSelectedQuestions(new Set());
      setDelegationNotes('');
      setShowDelegationForm(false);
      onQuestionsDelegated();
      if (onClose) {
        onClose();
      }
      
    } catch (error: any) {
      console.error('Failed to delegate questions:', error);
      toast.error('Failed to delegate questions. Please try again.');
    } finally {
      setIsDelegating(false);
    }
  };

  const selectedQuestionsList = questions.filter(q => selectedQuestions.has(q.questionId));

  return (
    <div className="bg-white rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <UserGroupIcon className="h-6 w-6 text-blue-500" />
          <h3 className="text-lg font-semibold text-gray-900">
            Delegate Questions to Client
          </h3>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        )}
      </div>

      {showDelegationForm && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Select questions to delegate to {assessment.client?.name} for completion.
          </p>

          {/* Question Selection */}
          <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-md">
            {questions.map((question, index) => (
              <div
                key={question.questionId}
                className={`p-3 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                  selectedQuestions.has(question.questionId) ? 'bg-blue-50' : ''
                }`}
                onClick={() => handleQuestionToggle(question.questionId)}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-1">
                    {selectedQuestions.has(question.questionId) ? (
                      <CheckIcon className="h-5 w-5 text-blue-600" />
                    ) : (
                      <div className="h-5 w-5 border-2 border-gray-300 rounded" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      Question {index + 1}
                    </p>
                    <p className="text-sm text-gray-600 truncate">
                      {question.questionText}
                    </p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                        {question.dimension?.name}
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                        {question.questionType}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Delegation Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Delegation Notes (Optional)
            </label>
            <textarea
              value={delegationNotes}
              onChange={(e) => setDelegationNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Add any specific instructions or context for the client..."
            />
          </div>

          {/* Selected Questions Summary */}
          {selectedQuestionsList.length > 0 && (
            <div className="bg-blue-50 rounded-md p-3">
              <p className="text-sm font-medium text-blue-900 mb-2">
                Selected Questions ({selectedQuestionsList.length}):
              </p>
              <div className="space-y-1">
                {selectedQuestionsList.map((question, index) => (
                  <div key={question.questionId} className="flex items-center space-x-2">
                    <span className="text-sm text-blue-800">
                      {index + 1}. {question.questionText.substring(0, 60)}...
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleQuestionToggle(question.questionId);
                      }}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                setShowDelegationForm(false);
                setSelectedQuestions(new Set());
                setDelegationNotes('');
                if (onClose) {
                  onClose();
                }
              }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDelegateQuestions}
              disabled={isDelegating || selectedQuestions.size === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isDelegating ? (
                <>
                  <ClockIcon className="h-4 w-4 animate-spin" />
                  <span>Delegating...</span>
                </>
              ) : (
                <>
                  <UserGroupIcon className="h-4 w-4" />
                  <span>Delegate {selectedQuestions.size} Questions</span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionDelegation;
